/**
 * Authentication utilities using Clerk + registro flow para VIP.
 *
 * El flujo VIP requiere:
 *   1. user.unsafeMetadata.{alias, telegram, birthDate}  (CompleteRegistration)
 *   2. user.publicMetadata.approved === true             (admin approve)
 *
 * Helpers portados del dashboard admin compartido entre brands del monorepo.
 */

import type { UserResource } from "@clerk/types";
import { useAuth, useUser } from "@clerk/clerk-react";
import { useMemo } from "react";
import {
  isActiveMembershipLevel,
  parseClerkSessionClaims,
  type ClerkSessionClaims,
} from "@/lib/clerkSessionClaims";

export type UserRole = "admin" | "user";

export const MIN_REGISTRATION_AGE = 18;

export interface RegistrationData {
  alias: string;
  birthDate: string; // ISO o YYYY-MM-DD
  telegram: string;
}

// ─── Role / admin checks ──────────────────────────────────────────────────────

/** True si publicMetadata otorga permisos de panel/API admin (alineado con api/lib/clerkAuth). */
export function isPublicMetadataAdmin(
  meta: Record<string, unknown> | null | undefined,
): boolean {
  if (!meta || typeof meta !== "object") return false;
  const fromMeta = parseClerkSessionClaims(meta);
  return fromMeta.role === "admin" || fromMeta.role === "super_admin";
}

const DEV_BYPASS = import.meta.env.VITE_ADMIN_DEV_BYPASS === "true";

/** Lee role desde Clerk publicMetadata */
function useUserRoleClerk(): UserRole {
  const { user } = useUser();
  const meta = user?.publicMetadata as Record<string, unknown> | undefined;
  return isPublicMetadataAdmin(meta) ? "admin" : "user";
}

function useUserRoleBypass(): UserRole {
  return "admin";
}

function useIsAdminClerk(): boolean {
  return useUserRoleClerk() === "admin";
}

function useIsAdminBypass(): boolean {
  return true;
}

/** Compile-time dual export so VITE_ADMIN_DEV_BYPASS never touches Clerk hooks. */
export const useUserRole: () => UserRole = DEV_BYPASS
  ? useUserRoleBypass
  : useUserRoleClerk;

export const useIsAdmin: () => boolean = DEV_BYPASS
  ? useIsAdminBypass
  : useIsAdminClerk;

/** Super admin: role super_admin o flags super_admin / is_super_admin (alineado con api/lib/clerkAuth). */
export function isSuperAdmin(user: UserResource | null | undefined): boolean {
  if (!user) return false;
  const meta = user.publicMetadata as Record<string, unknown> | undefined;
  if (!meta) return false;

  const fromMeta = parseClerkSessionClaims(meta);
  return fromMeta.role === "super_admin" || fromMeta.superAdmin === true;
}

/**
 * Helper to understand how to mark someone as super admin in Clerk.
 * 
 * Supported patterns in publicMetadata:
 * 
 * 1. Recommended (best with our limited JWT template):
 *    { "role": "super_admin", "status": "active" }
 * 
 * 2. Your current pattern (also fully supported):
 *    { "role": "admin", "is_super_admin": true, "status": "active" }
 * 
 * 3. Alternative flag:
 *    { "role": "admin", "super_admin": true, "status": "active" }
 * 
 * The system will correctly elevate role === "super_admin" or treat 
 * role === "admin" + super flag as super admin.
 */
export const SUPER_ADMIN_METADATA_EXAMPLES = {
  recommended: { role: "super_admin", status: "active" },
  withFlag: { role: "admin", is_super_admin: true, status: "active" },
  alternativeFlag: { role: "admin", super_admin: true, status: "active" },
};

/** Correct metadata for regular (non-admin / common) users */
export const REGULAR_USER_METADATA = {
  recommended: { role: "user", status: "pending" },
  // Once approved/membership active, it can be updated to "active"
  active: { role: "user", status: "active" },
  // Other possible: "expired", "suspended", "cancelled", "rejected"
};

function useIsSuperAdminClerk(): boolean {
  const { claims, isSignedIn } = useClerkSessionClaims();
  const { user } = useUser();

  if (!isSignedIn) return false;

  // Primary: from claims (JWT template) — fast path.
  // Our template only publishes role + status, so role can be "super_admin".
  if (claims.role === "super_admin" || claims.superAdmin === true) {
    return true;
  }

  // Strong fallback: inspect publicMetadata directly (via the parser which
  // exhaustively checks role, roles[], super_admin flags etc.).
  // This is critical because the JWT template is minimal and may not include
  // all flags on first paint / old tokens.
  if (user && isSuperAdmin(user)) {
    return true;
  }

  return false;
}

function useIsSuperAdminBypass(): boolean {
  return true;
}

/** Dev-only bypass: full admin without Clerk session (never set in prod). */
export const useIsSuperAdmin: () => boolean = DEV_BYPASS
  ? useIsSuperAdminBypass
  : useIsSuperAdminClerk;

export function isClerkAdmin(user: UserResource | null | undefined): boolean {
  if (!user) return false;
  return isPublicMetadataAdmin(user.publicMetadata as Record<string, unknown>);
}

// ─── Profile completion (registro VIP) ────────────────────────────────────────

/**
 * Returns true cuando el user tiene los campos del CompleteRegistration:
 * alias, birthDate, telegram en unsafeMetadata.
 *
 * Usado por:
 *   - CompleteRegistration: para no pedir el form si ya está completo
 *   - PendingApproval: para no quedarse loop si vuelve sin completar
 */
export function isProfileComplete(user: UserResource | null | undefined): boolean {
  if (!user) return false;
  // Admins skip — siempre considerados "completos"
  if (isClerkAdmin(user)) return true;
  const meta = (user.unsafeMetadata ?? {}) as Record<string, unknown>;
  const alias = typeof meta.alias === "string" ? meta.alias.trim() : "";
  const birthDate = typeof meta.birthDate === "string" ? meta.birthDate.trim() : "";
  const telegram = typeof meta.telegram === "string" ? meta.telegram.trim() : "";
  return alias.length >= 3 && birthDate.length >= 4 && telegram.length >= 3;
}

export function getRegistrationData(
  user: UserResource | null | undefined,
): RegistrationData {
  const meta = (user?.unsafeMetadata ?? {}) as Record<string, unknown>;
  return {
    alias: typeof meta.alias === "string" ? meta.alias : "",
    birthDate: typeof meta.birthDate === "string" ? meta.birthDate : "",
    telegram: typeof meta.telegram === "string" ? meta.telegram : "",
  };
}

// ─── Validation ───────────────────────────────────────────────────────────────

export function isAdultBirthDate(dateIsoOrYmd: string): boolean {
  if (!dateIsoOrYmd) return false;
  const parsed = new Date(
    dateIsoOrYmd.length === 10 ? `${dateIsoOrYmd}T00:00:00.000Z` : dateIsoOrYmd,
  );
  if (Number.isNaN(parsed.getTime())) return false;
  const now = new Date();
  let age = now.getFullYear() - parsed.getFullYear();
  const m = now.getMonth() - parsed.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < parsed.getDate())) age -= 1;
  return age >= MIN_REGISTRATION_AGE;
}

export function validateRegistrationData(input: RegistrationData): string | null {
  const alias = (input.alias ?? "").trim();
  const telegram = (input.telegram ?? "").trim();
  const birthDate = (input.birthDate ?? "").trim();
  if (alias.length < 3 || alias.length > 30) {
    return "El alias debe tener entre 3 y 30 caracteres.";
  }
  if (telegram.length < 3 || telegram.length > 50) {
    return "El usuario de Telegram debe tener entre 3 y 50 caracteres.";
  }
  if (!birthDate) {
    return "Ingresá tu fecha de nacimiento.";
  }
  if (!isAdultBirthDate(birthDate)) {
    return `Debes tener al menos ${MIN_REGISTRATION_AGE} años para continuar.`;
  }
  return null;
}

// ─── Approved check ───────────────────────────────────────────────────────────

/**
 * Aprobación admin para acceso a contenido privado (Telegram link).
 *
 * Comportamiento:
 *   - Admins → siempre aprobados.
 *   - publicMetadata.approved === true → aprobado.
 *   - publicMetadata.approved === false → NO aprobado (explícito).
 *   - Sin flag → NO aprobado (default seguro para nuevo flujo VIP).
 *
 * Nota: este cambio respecto a antes (que default era true) es intencional —
 * el flujo VIP requiere approval explícito del admin para asignar Telegram link.
 */
export function isUserApproved(user: UserResource | null | undefined): boolean {
  if (!user) return false;
  if (isClerkAdmin(user)) return true;
  return user.publicMetadata?.approved === true;
}

// ─── Display helpers ──────────────────────────────────────────────────────────

export function getUserDisplayName(
  user: UserResource | null | undefined,
): string {
  if (!user) return "";
  const meta = (user.unsafeMetadata ?? {}) as Record<string, unknown>;
  const alias = typeof meta.alias === "string" ? meta.alias.trim() : "";
  if (alias) return alias;
  if (user.firstName) return user.firstName;
  return user.primaryEmailAddress?.emailAddress?.split("@")[0] ?? "";
}

// ─── Session claims ────────────────────────────────────────────────────────────

/**
 * Lee los claims del JWT template de Clerk con fallback a `publicMetadata`.
 *
 * Preferencia:
 *   1. `useAuth().sessionClaims` — vienen en el token, sin fetch a Clerk API.
 *   2. `useUser().user.publicMetadata` — fallback cuando el token aún no se
 *      hidratan los claims (primer paint / tokens antiguos sin template).
 *
 * Email se complementa con `user.primaryEmailAddress?.emailAddress` porque
 * el built-in claim del JWT puede llegar vacío en algunos templates.
 */
function useClerkSessionClaimsClerk(): {
  claims: ClerkSessionClaims;
  isLoaded: boolean;
  isSignedIn: boolean;
} {
  const { isLoaded, isSignedIn, sessionClaims } = useAuth();
  const { user, isLoaded: userLoaded } = useUser();

  return useMemo(() => {
    if (!isLoaded || !userLoaded) {
      return { claims: {}, isLoaded: false, isSignedIn: false };
    }
    if (!isSignedIn || !user) {
      return { claims: {}, isLoaded: true, isSignedIn: false };
    }

    const fromToken = parseClerkSessionClaims(
      sessionClaims as Record<string, unknown> | null | undefined,
    );
    const fromMeta = parseClerkSessionClaims(
      user.publicMetadata as Record<string, unknown> | undefined,
    );

    // Merge campo-por-campo: el token gana SI trae el valor, sino cae al
    // publicMetadata. Antes hacíamos `{...fromMeta, ...fromToken}`, pero eso
    // dejaba que un `undefined` del token (ej. el JWT template no publica
    // `is_super_admin`) PISARA el valor correcto del publicMetadata → un
    // super-admin por flag `is_super_admin` no se reconocía en el front aunque
    // el backend sí. (token ?? meta) evita ese clobber.
    const merged: ClerkSessionClaims = {
      membershipLevel: fromToken.membershipLevel ?? fromMeta.membershipLevel,
      role: fromToken.role ?? fromMeta.role,
      banned: fromToken.banned ?? fromMeta.banned,
      approved: fromToken.approved ?? fromMeta.approved,
      superAdmin: fromToken.superAdmin ?? fromMeta.superAdmin,
      devilDarkUi: fromToken.devilDarkUi ?? fromMeta.devilDarkUi,
      telegramInviteUrl: fromToken.telegramInviteUrl ?? fromMeta.telegramInviteUrl,
      email: fromToken.email ?? fromMeta.email,
      // status and role come directly from the configured JWT template:
      // { "role": "{{user.public_metadata.role}}", "status": "{{user.public_metadata.status}}" }
      status: fromToken.status ?? fromMeta.status,
    };

    // El flag super_admin/is_super_admin eleva el role (consistente con
    // parseClerkSessionClaims y con api/lib/clerkAuth.ts del backend).
    if (merged.superAdmin) merged.role = "super_admin";
    if (merged.role === "super_admin") merged.superAdmin = true;

    if (!merged.email) {
      merged.email = user.primaryEmailAddress?.emailAddress?.toLowerCase();
    }

    return { claims: merged, isLoaded: true, isSignedIn: true };
  }, [isLoaded, userLoaded, isSignedIn, user, sessionClaims]);
}

const DEV_BYPASS_CLAIMS: ClerkSessionClaims = {
  role: "super_admin",
  superAdmin: true,
  approved: true,
  status: "active",
  email: "dev-bypass@local",
};

function useClerkSessionClaimsBypass(): {
  claims: ClerkSessionClaims;
  isLoaded: boolean;
  isSignedIn: boolean;
} {
  return { claims: DEV_BYPASS_CLAIMS, isLoaded: true, isSignedIn: true };
}

export const useClerkSessionClaims: () => {
  claims: ClerkSessionClaims;
  isLoaded: boolean;
  isSignedIn: boolean;
} = DEV_BYPASS ? useClerkSessionClaimsBypass : useClerkSessionClaimsClerk;

export function useMembershipLevel(): string | null {
  const { claims } = useClerkSessionClaims();
  const lvl = claims.membershipLevel?.toLowerCase()?.trim();
  return lvl && isActiveMembershipLevel(lvl) ? lvl : null;
}

export function useIsBanned(): boolean {
  const { claims, isSignedIn } = useClerkSessionClaims();
  return isSignedIn && claims.banned === true;
}

export function useTelegramInviteUrl(): string | null {
  const { claims } = useClerkSessionClaims();
  return claims.telegramInviteUrl ?? null;
}

export function useUserStatus(): string | null {
  const { claims } = useClerkSessionClaims();
  return claims.status ?? null;
}

/**
 * `devil_dark_ui` se asigna **manualmente** por el equipo (admin / Clerk Dashboard).
 * No lo derivamos de `membership_level` para evitar pisar la decisión humana.
 * Por default queda deshabilitado.
 */
export function useDevilDarkUi(): boolean {
  const { claims, isSignedIn } = useClerkSessionClaims();
  if (!isSignedIn) return false;
  return claims.devilDarkUi === true;
}
